// ==========================
//  ClusterTopology.jsx
// ==========================

import { useCallback, useEffect, useState, useMemo } from "react";
import ReactFlow, {
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  Panel,
  Handle,
  Position,
  BaseEdge,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";

import { Box, Button, Switch, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { clusterSupabase } from "../../../../components/SupabaseClient.js";
import {
  ACCENT,
  SURFACE,
  HAIRLINE,
  HAIRLINE_STRONG,
} from "../../../../theme/tokens.js";
import "./ClusterTopology.css";

// Node/edge palette for the minimap + legend (kept in sync with the CSS)
const COLOR_SERVER = HAIRLINE_STRONG; // neutral slate
const COLOR_SWITCH = ACCENT; // accent / network gear
const COLOR_POD = "#A7D3C7"; // light accent
const COLOR_LINK = "#94A3B8"; // ink-muted

// iOS-style Switch (shared with the canvas toolbar)
const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "200ms",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: ACCENT,
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
    opacity: 1,
    boxSizing: "border-box",
  },
}));

// Canvas toolbar (top-right): view-level controls
function TopologyToolbar({
  highlightRelatedEdges,
  onHighlightChange,
  onRecenter,
  recenterLabel,
}) {
  return (
    <div className="topo-toolbar">
      <div className="topo-toolbar-row">
        <Typography
          variant="body2"
          sx={{ fontSize: 12, color: "text.secondary" }}
        >
          Highlight links
        </Typography>
        <IOSSwitch
          checked={highlightRelatedEdges}
          onChange={(e) => onHighlightChange?.(e.target.checked)}
        />
      </div>
      <Button
        size="small"
        variant="text"
        onClick={onRecenter}
        sx={{
          alignSelf: "stretch",
          justifyContent: "flex-start",
          minHeight: 24,
          px: 0,
          py: 0,
          fontSize: 12,
          fontWeight: 600,
          textTransform: "none",
        }}
      >
        Re-center: {recenterLabel}
      </Button>
    </div>
  );
}

// Canvas legend (top-left): single row of swatches, no title
function TopologyLegend() {
  return (
    <div className="topo-legend">
      <div className="topo-legend-row">
        <span
          className="topo-legend-swatch"
          style={{ background: "#F1F5F9", border: `1px solid ${COLOR_SERVER}` }}
        />
        Server
      </div>
      <div className="topo-legend-row">
        <span
          className="topo-legend-swatch"
          style={{ background: "#E6F2EE", border: `1px solid ${COLOR_SWITCH}` }}
        />
        Switch
      </div>
      <div className="topo-legend-row">
        <span
          className="topo-legend-line"
          style={{ borderTop: `2px solid ${COLOR_LINK}` }}
        />
        Link
      </div>
      <div className="topo-legend-row">
        <span
          className="topo-legend-line"
          style={{ borderTop: `3px solid ${COLOR_SWITCH}` }}
        />
        Highlighted link
      </div>
    </div>
  );
}

// ===== Layout constants =====
// Horizontal distance between racks
const RACK_X_GAP = 350;
// Height of each RU in pixels
const ROW_H = 24;
// Width of the RU label column
const RU_LABEL_WIDTH = 40;
// Y offset where the server area starts inside each rack
const SERVER_START_OFFSET = 80;
// Left offset of the server area inside the rack (to leave room for RU labels)
const SERVER_LEFT_OFFSET = RU_LABEL_WIDTH + 28;
// Total width of the rack container
const RACK_WIDTH = 280;
// Bottom padding of the rack container
const RACK_BOTTOM_PADDING = 20;
// Vertical margin for server/switch nodes inside each RU block
const NODE_V_MARGIN = 2;
// Height of the top “pod wires” bar
const POD_BAR_HEIGHT = 80;
// Vertical gap between the top bar and racks
const POD_BAR_MARGIN = 16;
// Horizontal offset for edges so they route out from the side of nodes
const EDGE_H_OFFSET = 18;
// Corner radius for custom edge bends
const EDGE_CORNER_RADIUS = 6;

// Hidden handle style (we need anchors but do not want them visible)
const hiddenHandleStyle = {
  opacity: 0,
  pointerEvents: "none",
};

/* ================= Custom Edge components ================= */
// In-rack connections (between server/switch within the same rack)
const InRackEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style = {},
  markerEnd,
  interactionWidth,
}) => {
  // Route edges along a shared “bus” on the left side of the nodes
  const busX = Math.min(sourceX, targetX) - EDGE_H_OFFSET;
  const goingDown = targetY > sourceY ? 1 : -1;
  const r = EDGE_CORNER_RADIUS * goingDown;

  const path = `
    M ${sourceX},${sourceY}
    L ${busX + EDGE_CORNER_RADIUS},${sourceY}
    Q ${busX},${sourceY} ${busX},${sourceY + r}
    L ${busX},${targetY - r}
    Q ${busX},${targetY} ${busX + EDGE_CORNER_RADIUS},${targetY}
    L ${targetX},${targetY}
  `;

  return (
    <BaseEdge
      id={id}
      path={path}
      style={style}
      markerEnd={markerEnd}
      interactionWidth={interactionWidth}
    />
  );
};

// Cross-rack connections: from device nodes up to the top pod wires bar
const CrossRackEdge = ({
  id,
  sourceX,
  sourceY,
  targetY,
  style = {},
  markerEnd,
  interactionWidth,
}) => {
  const cornerX = sourceX + EDGE_H_OFFSET;
  const goingDown = targetY > sourceY ? 1 : -1;
  const r = EDGE_CORNER_RADIUS * goingDown;

  const path = `
    M ${sourceX},${sourceY}
    L ${cornerX - EDGE_H_OFFSET / 2},${sourceY}
    Q ${cornerX},${sourceY} ${cornerX},${sourceY + r}
    L ${cornerX},${targetY}
  `;

  return (
    <BaseEdge
      id={id}
      path={path}
      style={style}
      markerEnd={markerEnd}
      interactionWidth={interactionWidth}
    />
  );
};

// Edge type registry for ReactFlow
const edgeTypes = {
  inRack: InRackEdge,
  crossRack: CrossRackEdge,
};

// ===== Utility functions =====
// Check if a device is a switch
function isSwitchDevice(device) {
  if (!device.role) return false;
  const r = device.role.toLowerCase();
  return r.includes("switch") || r.includes("tor");
}

// ===== Custom node components =====
// Top pod bar to represent cross-rack wiring
function PodWiresNode({ data }) {
  return (
    <div
      className="pod-wires-node"
      style={{ width: data.widthPx, height: POD_BAR_HEIGHT }}
    >
      <span className="pod-wires-title">Pod cross-rack links</span>
      {data.subtitle && (
        <span className="pod-wires-subtitle">{data.subtitle}</span>
      )}
      <Handle
        type="target"
        position={Position.Bottom}
        style={hiddenHandleStyle}
      />
    </div>
  );
}

// Rack background node (renders rack frame and RU labels, no interaction)
function RackNode({ data }) {
  const rowCount = data.rackHeightRu ?? 42;
  const heightPx = data.heightPx;
  const ruAreaHeight = rowCount * ROW_H;

  return (
    <div className="rack-node" style={{ height: heightPx, width: RACK_WIDTH }}>
      <div className="rack-title">{data.label}</div>
      <div className="rack-info" style={{ height: SERVER_START_OFFSET - 36 }}>
        Spec: {rowCount} RU Rack
      </div>
      <div className="rack-ru-labels" style={{ height: ruAreaHeight }}>
        {Array.from({ length: rowCount }).map((_, j) => {
          const ruNumber = rowCount - j - 1;
          return (
            <div
              className="rack-ru-label"
              key={ruNumber}
              style={{ height: ROW_H }}
            >
              {ruNumber}
            </div>
          );
        })}
      </div>
      <div
        className="rack-ru-bg"
        style={{
          height: ruAreaHeight,
          top: SERVER_START_OFFSET,
          left: RU_LABEL_WIDTH,
        }}
      />
    </div>
  );
}

// Switch node inside a rack
function SwitchNode({ data }) {
  // Simple ripple visual effect when clicking the node
  const onClick = (e) => {
    const target = e.currentTarget;
    const circle = document.createElement("span");
    const diameter = Math.max(target.clientWidth, target.clientHeight);
    const radius = diameter / 2;
    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${
      e.clientX - target.getBoundingClientRect().left - radius
    }px`;
    circle.style.top = `${
      e.clientY - target.getBoundingClientRect().top - radius
    }px`;
    circle.classList.add("ripple");
    const ripple = target.getElementsByClassName("ripple")[0];
    if (ripple) ripple.remove();
    target.appendChild(circle);
  };

  return (
    <div
      className="switch-node ripple-container"
      style={{ height: data.heightPx }}
      onClick={onClick}
    >
      <div className="switch-title">{data.label}</div>
      {data.details?.ip && <div className="switch-ip">{data.details.ip}</div>}
      <Handle
        id="left-source"
        type="source"
        position={Position.Left}
        style={hiddenHandleStyle}
      />
      <Handle
        id="left-target"
        type="target"
        position={Position.Left}
        style={hiddenHandleStyle}
      />
      <Handle
        id="right-source"
        type="source"
        position={Position.Right}
        style={hiddenHandleStyle}
      />
    </div>
  );
}

// Server node inside a rack
function ServerNode({ data }) {
  // Same ripple effect as switch nodes
  const onClick = (e) => {
    const target = e.currentTarget;
    const circle = document.createElement("span");
    const diameter = Math.max(target.clientWidth, target.clientHeight);
    const radius = diameter / 2;
    circle.style.width = circle.style.height = `${diameter}px`;
    circle.style.left = `${
      e.clientX - target.getBoundingClientRect().left - radius
    }px`;
    circle.style.top = `${
      e.clientY - target.getBoundingClientRect().top - radius
    }px`;
    circle.classList.add("ripple");
    const ripple = target.getElementsByClassName("ripple")[0];
    if (ripple) ripple.remove();
    target.appendChild(circle);
  };

  return (
    <div
      className="server-node ripple-container"
      style={{ height: data.heightPx }}
      onClick={onClick}
    >
      <div className="server-title">{data.label}</div>
      {data.heightPx >= ROW_H * 1.5 && data.details?.ip && (
        <div className="server-ip">{data.details.ip}</div>
      )}
      <Handle
        id="left-source"
        type="source"
        position={Position.Left}
        style={hiddenHandleStyle}
      />
      <Handle
        id="left-target"
        type="target"
        position={Position.Left}
        style={hiddenHandleStyle}
      />
      <Handle
        id="right-source"
        type="source"
        position={Position.Right}
        style={hiddenHandleStyle}
      />
    </div>
  );
}

/* ============ Topology construction logic ============ */
// Build ReactFlow nodes and edges from raw DB data
function buildTopology(rawData, selectedPodId) {
  let { racks, devices, connections } = rawData;

  // If selectedPodId is provided, keep only racks/devices/connections in that pod
  if (selectedPodId != null) {
    const racksInPod = racks.filter((r) => r.pod_id === selectedPodId);
    const rackIdsInPod = new Set(racksInPod.map((r) => r.rack_id));
    const devicesInPod = devices.filter((d) => rackIdsInPod.has(d.rack_id));
    const deviceIdsInPod = new Set(devicesInPod.map((d) => d.device_id));
    const connectionsInPod = connections.filter(
      (c) =>
        deviceIdsInPod.has(c.src_dev_id) && deviceIdsInPod.has(c.dst_dev_id),
    );
    racks = racksInPod;
    devices = devicesInPod;
    connections = connectionsInPod;
  }

  const nodes = [];
  const edges = [];
  const sortedRacks = [...racks].sort((a, b) =>
    (a.rack_name || `Rack ${a.rack_id}`).localeCompare(
      b.rack_name || `Rack ${b.rack_id}`,
    ),
  );
  const rackIndexById = new Map();
  const rackNameById = new Map(
    sortedRacks.map((r) => [r.rack_id, r.rack_name || `Rack ${r.rack_id}`]),
  );

  // Build rack nodes
  sortedRacks.forEach((rack, idx) => {
    rackIndexById.set(rack.rack_id, idx);
    const rowCount = rack.rack_height_ru || 42;
    const heightPx =
      SERVER_START_OFFSET + rowCount * ROW_H + RACK_BOTTOM_PADDING;
    const x = idx * RACK_X_GAP;
    const y = POD_BAR_HEIGHT + POD_BAR_MARGIN;

    nodes.push({
      id: `rack-${rack.rack_id}`,
      type: "rack",
      position: { x, y },
      data: {
        label: rack.rack_name || `Rack ${rack.rack_id}`,
        rackHeightRu: rowCount,
        heightPx,
      },
      draggable: false,
      selectable: false,
      zIndex: -1,
    });
  });

  // Group devices by rack
  const devicesByRack = new Map();
  devices.forEach((d) => {
    if (!devicesByRack.has(d.rack_id)) devicesByRack.set(d.rack_id, []);
    devicesByRack.get(d.rack_id).push(d);
  });
  const devIdToNodeId = new Map();

  // Place devices into the appropriate RU position in each rack
  sortedRacks.forEach((rack) => {
    const rackDevices = devicesByRack.get(rack.rack_id) || [];
    const rackX = (rackIndexById.get(rack.rack_id) || 0) * RACK_X_GAP;
    const rowCount = rack.rack_height_ru || 42;

    const switches = [];
    const servers = [];
    rackDevices.forEach((d) => {
      if (isSwitchDevice(d)) switches.push(d);
      else servers.push(d);
    });

    // Switch nodes
    switches.forEach((d) => {
      const id = `dev-${d.device_id}`;
      devIdToNodeId.set(d.device_id, id);
      const posIndex = d.rack_pos_ru ?? 0;
      const h = d.height_ru ?? 1;
      const topRowIndex = rowCount - posIndex - h;
      const blockTop =
        POD_BAR_HEIGHT +
        POD_BAR_MARGIN +
        SERVER_START_OFFSET +
        topRowIndex * ROW_H;
      const blockHeight = h * ROW_H;

      nodes.push({
        id,
        type: "switch",
        position: {
          x: rackX + SERVER_LEFT_OFFSET,
          y: blockTop + NODE_V_MARGIN,
        },
        data: {
          label: d.hostname || `Switch ${d.device_id}`,
          heightPx: blockHeight - NODE_V_MARGIN * 2,
          details: {
            id: d.device_id,
            ip: d.properties?.ip || null,
            role: d.role || null,
            rackName: rackNameById.get(d.rack_id) || null,
            rackPosRu: d.rack_pos_ru ?? null,
            heightRu: d.height_ru ?? null,
          },
        },
        zIndex: 15,
      });
    });

    // Server nodes
    servers.forEach((d) => {
      const id = `dev-${d.device_id}`;
      devIdToNodeId.set(d.device_id, id);
      const posIndex = d.rack_pos_ru ?? 0;
      const h = d.height_ru ?? 1;
      const topRowIndex = rowCount - posIndex - h;
      const blockTop =
        POD_BAR_HEIGHT +
        POD_BAR_MARGIN +
        SERVER_START_OFFSET +
        topRowIndex * ROW_H;
      const blockHeight = h * ROW_H;

      nodes.push({
        id,
        type: "server",
        position: {
          x: rackX + SERVER_LEFT_OFFSET,
          y: blockTop + NODE_V_MARGIN,
        },
        data: {
          label: d.hostname || `Server ${d.device_id}`,
          heightPx: blockHeight - NODE_V_MARGIN * 2,
          details: {
            id: d.device_id,
            ip: d.properties?.ip || null,
            role: d.role || null,
            rackName: rackNameById.get(d.rack_id) || null,
            rackPosRu: d.rack_pos_ru ?? null,
            heightRu: d.height_ru ?? null,
          },
        },
        zIndex: 10,
      });
    });
  });

  // Top pod wires bar spanning all racks in this pod
  if (sortedRacks.length > 0) {
    const firstX = 0;
    const lastX = (sortedRacks.length - 1) * RACK_X_GAP;
    const totalWidth = lastX - firstX + RACK_WIDTH + 16;
    nodes.push({
      id: "pod-wires",
      type: "podWires",
      position: { x: firstX - 8, y: 0 },
      data: { widthPx: totalWidth },
      draggable: false,
      selectable: false,
      zIndex: 5,
    });
  }

  // Build edge (link) information
  const deviceById = new Map();
  devices.forEach((d) => deviceById.set(d.device_id, d));

  connections.forEach((c) => {
    const fromNodeId = devIdToNodeId.get(c.src_dev_id);
    const toNodeId = devIdToNodeId.get(c.dst_dev_id);
    if (!fromNodeId || !toNodeId) return;

    const srcDev = deviceById.get(c.src_dev_id);
    const dstDev = deviceById.get(c.dst_dev_id);

    const inRack =
      srcDev.rack_id != null &&
      dstDev.rack_id != null &&
      srcDev.rack_id === dstDev.rack_id;

    const data = {
      connectionId: c.id,
      srcNodeId: fromNodeId,
      dstNodeId: toNodeId,
    };

    if (inRack) {
      // In-rack connections: use the custom inRack edge type
      edges.push({
        id: `edge-${c.id}`,
        source: fromNodeId,
        target: toNodeId,
        type: "inRack",
        sourcePosition: "left",
        targetPosition: "left",
        sourceHandle: "left-source",
        targetHandle: "left-target",
        className: "edge-base",
        data,
      });
    } else {
      // Cross-rack connections: connect both endpoints to the top pod wires bar
      edges.push({
        id: `edge-${c.id}-src`,
        source: fromNodeId,
        target: "pod-wires",
        type: "crossRack",
        sourcePosition: "right",
        targetPosition: "bottom",
        sourceHandle: "right-source",
        className: "edge-base",
        data,
      });
      edges.push({
        id: `edge-${c.id}-dst`,
        source: toNodeId,
        target: "pod-wires",
        type: "crossRack",
        sourcePosition: "right",
        targetPosition: "bottom",
        sourceHandle: "right-source",
        className: "edge-base",
        data,
      });
    }
  });

  return { nodes, edges };
}

/* ============ Main component ============ */
export default function ClusterTopology({
  selectedPodId,
  selectedRackId,
  selectedNodeId,
  recenterRequest = 0,
  onNodeSelect,
  highlightRelatedEdges = false,
  onHighlightChange,
}) {
  // Raw data: table contents fetched from Supabase
  const [rawData, setRawData] = useState({
    racks: [],
    devices: [],
    connections: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Nodes/edges used by ReactFlow
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);

  // ReactFlow instance for imperative actions (e.g., fitView)
  const { fitView, getNode } = useReactFlow();

  // Hovered/selected connection (identified by connectionId)
  const [hoveredConnectionId, setHoveredConnectionId] = useState(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState(null);

  // --- Fetch data from Supabase when component mounts ---
  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setLoading(true);
      setError("");
      try {
        const [racksRes, devicesRes, connsRes] = await Promise.all([
          clusterSupabase.from("Racks").select("*"),
          clusterSupabase.from("Devices").select("*"),
          clusterSupabase.from("Connections").select("*"),
        ]);

        if (racksRes.error) throw racksRes.error;
        if (devicesRes.error) throw devicesRes.error;
        if (connsRes.error) throw connsRes.error;

        if (!cancelled) {
          setRawData({
            racks: racksRes.data || [],
            devices: devicesRes.data || [],
            connections: connsRes.data || [],
          });
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load topology data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, []);

  // --- Rebuild topology when raw data or selected pod changes ---
  useEffect(() => {
    const { nodes, edges } = buildTopology(rawData, selectedPodId);
    setNodes(nodes);
    setEdges(edges);
    setHoveredConnectionId(null);
    setSelectedConnectionId(null);
  }, [rawData, selectedPodId]);

  const selectedRackLabel = useMemo(() => {
    if (!selectedRackId) return "All racks";
    const rack = rawData.racks.find(
      (r) => String(r.rack_id) === String(selectedRackId),
    );
    return rack?.rack_name || `Rack ${selectedRackId}`;
  }, [rawData.racks, selectedRackId]);

  const recenterView = useCallback(() => {
    if (selectedRackId) {
      const rackNode =
        getNode(`rack-${selectedRackId}`) ||
        nodes.find((node) => node.id === `rack-${selectedRackId}`);
      if (!rackNode) return;

      fitView({
        nodes: [rackNode],
        duration: 300,
        padding: 0.4,
      });
    } else {
      fitView({ duration: 300, padding: 0.2 });
    }
  }, [fitView, getNode, nodes, selectedRackId]);

  // --- Focus on a specific rack (or fit all) when the selected rack changes ---
  useEffect(() => {
    recenterView();
  }, [recenterView]);

  useEffect(() => {
    if (!recenterRequest) return;

    let secondFrame;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        recenterView();
      });
    });

    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [recenterRequest, recenterView]);

  useEffect(() => {
    if (!selectedNodeId) return;

    const node = nodes.find((n) => n.id === selectedNodeId);
    if (!node) return;

    setSelectedConnectionId(null);
    onNodeSelect?.(node);
  }, [nodes, onNodeSelect, selectedNodeId]);

  // Inject selected flag into nodes for ReactFlow (used for highlighting)
  const displayNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        selected: node.id === selectedNodeId,
      })),
    [nodes, selectedNodeId],
  );

  // Node type registry for ReactFlow
  const nodeTypes = useMemo(
    () => ({
      podWires: PodWiresNode,
      rack: RackNode,
      switch: SwitchNode,
      server: ServerNode,
    }),
    [],
  );

  /* ===== Compute which connections should be highlighted =====
     Rules:
     - edge under mouse hover
     - clicked/selected edge
     - (optional) all links related to the currently selected node
  */
  const highlightedConnectionIds = useMemo(() => {
    const set = new Set();

    if (hoveredConnectionId != null) set.add(hoveredConnectionId);
    if (selectedConnectionId != null) set.add(selectedConnectionId);

    if (highlightRelatedEdges && selectedNodeId != null) {
      edges.forEach((e) => {
        const connId = e.data?.connectionId;
        if (!connId) return;
        const { srcNodeId, dstNodeId } = e.data;
        if (srcNodeId === selectedNodeId || dstNodeId === selectedNodeId) {
          set.add(connId);
        }
      });
    }

    return set;
  }, [
    hoveredConnectionId,
    selectedConnectionId,
    highlightRelatedEdges,
    selectedNodeId,
    edges,
  ]);

  /* ===== Build edges based on highlight state (attach className) ===== */
  const displayEdges = useMemo(
    () =>
      edges.map((e) => {
        const connId = e.data?.connectionId;
        const isHighlighted = connId && highlightedConnectionIds.has(connId);
        const isSelected = connId && connId === selectedConnectionId;

        return {
          ...e,
          // interactionWidth enlarges the clickable area of the edge
          interactionWidth: 80,
          className: [
            "edge-base",
            isHighlighted ? "edge-highlighted" : "",
            isSelected ? "selected" : "",
          ]
            .filter(Boolean)
            .join(" "),
        };
      }),
    [edges, highlightedConnectionIds, selectedConnectionId],
  );

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 4, color: "error.main", textAlign: "center" }}>{error}</Box>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Box sx={{ p: 4, color: "text.secondary", textAlign: "center" }}>
        Loading cluster topology...
      </Box>
    );
  }

  return (
    <div style={{ width: "100%", height: "100%", background: SURFACE }}>
      <ReactFlow
        nodes={displayNodes}
        edges={displayEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.2}
        maxZoom={4}
        // Click node: update selected node and pass info to outer (details panel)
        onNodeClick={(e, node) => {
          // rack / podWires are background/decoration — treat as "clear selection"
          if (node.type === "rack" || node.type === "podWires") {
            setSelectedConnectionId(null);
            if (onNodeSelect) onNodeSelect(null);
            return;
          }

          setSelectedConnectionId(null);

          if (
            onNodeSelect &&
            (node.type === "server" || node.type === "switch")
          ) {
            onNodeSelect(node);
          } else if (onNodeSelect) {
            // any other node type shouldn't count as "device selected"
            onNodeSelect(null);
          }
        }}
        // Mouse enters an edge: remember connectionId for highlight
        onEdgeMouseEnter={(event, edge) => {
          const connId = edge.data?.connectionId ?? null;
          if (connId != null) setHoveredConnectionId(connId);
        }}
        onEdgeMouseLeave={() => setHoveredConnectionId(null)}
        // Click on edge: record selected connectionId, clear device selection
        onEdgeClick={(event, edge) => {
          const connId = edge.data?.connectionId ?? null;
          setSelectedConnectionId(connId);
          if (onNodeSelect) onNodeSelect(null);
        }}
        // Click on empty pane: clear selection/hover state for nodes and edges
        onPaneClick={() => {
          setSelectedConnectionId(null);
          setHoveredConnectionId(null);
          if (onNodeSelect) onNodeSelect(null);
        }}
      >
        {/* Faint dot grid: signals a pannable canvas without competing with
            the cables (dots read as texture, lines would read as wires). */}
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={2}
          color="#9da7b5"
        />
        {/* Single full-width top bar: legend on the left, toolbar pushed to
            the right. When the canvas is too narrow to fit both, the toolbar
            wraps onto the next line instead of overlapping the legend. */}
        <Panel position="top-left" className="topo-topbar-panel">
          <div className="topo-topbar">
            <TopologyLegend />
            <TopologyToolbar
              highlightRelatedEdges={highlightRelatedEdges}
              onHighlightChange={onHighlightChange}
              onRecenter={recenterView}
              recenterLabel={selectedRackLabel}
            />
          </div>
        </Panel>
        <MiniMap
          pannable
          zoomable
          nodeStrokeWidth={3}
          nodeColor={(n) => {
            if (n.type === "server") return COLOR_SERVER;
            if (n.type === "switch") return COLOR_SWITCH;
            if (n.type === "podWires") return COLOR_POD;
            return HAIRLINE;
          }}
        />
        <Controls />
      </ReactFlow>
    </div>
  );
}
